import { features, useThunkDispatch } from '@tunasong/redux'
import type { Agent, Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { AgentPill } from './agent-pill.js'

// Re-exports for the `/integration` subpath. The app shell's sidebar
// (`SidebarAgents`) consumes these to render the agent chat tool without
// depending on plugin internals — as does `@tunasong/ui-layouts`' AI-chat
// layout, which finds-or-creates its advisor agent via `resolveOrAttachAgent`
// and renders it with the same `AgentChat` surface.
//
// Co-locating the re-exports here means the package's `/integration`
// export entry (in `package.json`) doesn't need a separate barrel file
// — the agent plugin's whole "integrate me into a host shell" surface
// lives in this one module. The react-refresh warning that this triggers
// (non-component exports from a component file) is accepted: HMR for
// these hooks is a non-goal and the alternative — a parallel barrel
// pointed at by the package export — is more friction than the win.
/* eslint-disable react-refresh/only-export-components */
export { useAgentChildren } from './use-agent-children.js'
export { ProposalsHeader } from './proposals-header.js'
export { AgentChat } from './agent-chat.js'
export { resolveOrAttachAgent } from './resolve-or-attach-agent.js'
export {
  resolveDefaultColor,
  resolveDefaultIconKey,
  resolveDefaultPrompts,
  resolveDefaultRoleLine,
} from './role-defaults.js'
export { getAgentAccessibleName, getAgentDisplayName, resolveAgentIcon, resolveAgentVisuals } from './agent-visuals.js'
export type { AgentVisuals } from './agent-visuals.js'
export { useAgentTurnStream } from './use-agent-turn-stream.js'
export type { AgentTurnStream } from './use-agent-turn-stream.js'
export { useBlockActions } from './use-block-actions.js'
export type { BlockActionsOptions } from './use-block-actions.js'
export { blockRefsAt } from './cursor-block-refs.js'
export { useAgentRoster } from './use-agent-roster.js'
export { useAgentUnreadFlags } from './use-agent-unread-flags.js'
export type { AgentUnreadFlags } from './use-agent-unread-flags.js'
export type { AgentRoster, AgentRosterRow } from './use-agent-roster.js'
export { AgentsSubmenu } from './agents-submenu.js'
export { usePersonalizeAgent } from './personalize/use-personalize-agent.js'
export { useNewConversation } from './use-new-conversation.js'
export { personalizeCopy } from './personalize/personalize-copy.js'
/* eslint-enable react-refresh/only-export-components */

export interface AgentIntegrationProps {
  entity: Persisted<Entity> | null | undefined
  /** Gate the integration behind a feature flag. */
  enabled?: boolean
  className?: string
}

/**
 * Single mount point for the per-entity agents in the entity header: one
 * Agents pill (stacked initials, one unread count) whose menu lists each
 * agent. Choosing an agent surfaces the Agents tool in the sidebar with that
 * agent's chat thread active; the panel itself lives in the sidebar
 * (`SidebarAgents` in `apps/tunasong/tunasong-ui/src/sidebar/sidebar-agents.tsx`).
 *
 * Renders nothing without agents; the sidebar's Agents tool and the
 * `agent-toggle-panel` global command (default Cmd+Shift+J) stay available.
 *
 * Per-block proposal cues live on the `ProposalGutterChip` rendered by
 * the agent plugin's `BlockDecoration` slot, not here.
 *
 * Plan: docs/superpowers/plans/2026-10-10-song-main-declutter-plan.md (PR 5).
 */
export const AgentIntegration: FC<AgentIntegrationProps> = ({ entity, enabled = true, className }) => {
  const dispatch = useThunkDispatch()

  const handleSelect = (agent: Persisted<Agent>) => {
    dispatch(features.ui.actions.openAgentComposer({ agentId: agent.id }))
  }

  if (!enabled) {
    return null
  }

  return <AgentPill parentId={entity?.id} onSelect={handleSelect} className={className} />
}
